import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Icon from '../components/AppIcon';

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const finishCheck = (session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      setFullName(session?.user?.user_metadata?.full_name || '');
      setCheckingSession(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === 'SIGNED_IN'
        || event === 'PASSWORD_RECOVERY'
        || event === 'INITIAL_SESSION'
        || event === 'TOKEN_REFRESHED'
      ) {
        finishCheck(session);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      finishCheck(session);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!user) {
      setError('Invitation link is invalid or expired. Ask your manager to send a new invite.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: {
          ...user?.user_metadata,
          full_name: fullName.trim(),
        },
      });
      if (updateError) throw updateError;

      const invitedRestaurantId =
        user?.user_metadata?.invited_restaurant_id
        || user?.user_metadata?.restaurant_id
        || null;
      const invitedRole = user?.user_metadata?.invited_role || user?.user_metadata?.role || 'restaurant_admin';
      const normalizedRole = invitedRole === 'staff' ? 'staff' : 'restaurant_admin';
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          email: user.email,
          full_name: fullName.trim() || user?.user_metadata?.full_name || '',
          role: normalizedRole,
          ...(invitedRestaurantId ? { restaurant_id: invitedRestaurantId } : {}),
        }, { onConflict: 'id' });
      if (profileError) {
        console.error('Failed to link invited user to restaurant:', profileError);
      }

      if (invitedRestaurantId) {
        const { error: memberError } = await supabase
          .from('restaurant_members')
          .upsert(
            {
              user_id: user.id,
              restaurant_id: invitedRestaurantId,
              role: normalizedRole,
            },
            { onConflict: 'user_id,restaurant_id' },
          );
        if (memberError) {
          console.error('Failed to create restaurant membership:', memberError);
        }
        // SECURITY DEFINER sync repairs profile <-> membership even when RLS blocks client writes
        const { error: syncError } = await supabase.rpc('sync_my_restaurant_access');
        if (syncError && syncError?.code !== 'PGRST202' && syncError?.code !== '42883') {
          console.error('Failed to sync restaurant access after invite:', syncError);
        }
      }

      const redirectSlug = searchParams.get('slug');
      const redirectRestaurantId = searchParams.get('restaurant_id') || invitedRestaurantId;
      if (redirectSlug) {
        navigate(`/dashboard?slug=${encodeURIComponent(redirectSlug)}`, { replace: true });
      } else if (redirectRestaurantId) {
        navigate(`/dashboard?restaurant_id=${encodeURIComponent(redirectRestaurantId)}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err?.message || 'Failed to complete account setup.');
    } finally {
      setSubmitting(false);
    }
  };

  const slug = searchParams.get('slug');

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
          <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={22} className="text-destructive" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Invitation link expired</h1>
          <p className="text-sm text-muted-foreground mb-6">
            This invite link is invalid or has already been used. Ask your restaurant admin to send a new invitation.
          </p>
          <Button onClick={() => navigate(slug ? `/dashboard?slug=${slug}` : '/restaurant-admin-dashboard')}>
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="UserPlus" size={24} className="text-primary" />
          </div>
          <h1 className="text-xl font-bold text-foreground">Set up your account</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create a password for <strong>{user.email}</strong> to access the restaurant dashboard.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Full name</label>
            <Input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Confirm password</label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat password"
              required
              minLength={8}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Saving...' : 'Activate account'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvite;
